import { getSlideCaseAssignmentOptions, SlideCaseAssignmentOptionsRequest } from 'api/slides';
import { chunk } from 'es-toolkit/compat';
import { SlideCaseAssignmentItem } from 'interfaces/slide';

export const loadSlideCaseAssignmentOptions = async (
  request: Omit<SlideCaseAssignmentOptionsRequest, 'cursor'>,
  signal?: AbortSignal,
  onProgress?: (loadedCount: number) => void
): Promise<SlideCaseAssignmentItem[]> => {
  const items: SlideCaseAssignmentItem[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = null;

  do {
    if (signal?.aborted) {
      throw new DOMException('Slide selection loading was canceled.', 'AbortError');
    }
    const page = await getSlideCaseAssignmentOptions({ ...request, cursor: cursor ?? undefined }, signal);
    if (signal?.aborted) {
      throw new DOMException('Slide selection loading was canceled.', 'AbortError');
    }
    cursor = page.nextCursor;
    if (cursor !== null) {
      if (typeof cursor !== 'string' || cursor.length === 0) {
        throw new Error('Invalid slide selection continuation cursor.');
      }
      if (seenCursors.has(cursor)) {
        throw new Error('Slide selection continuation cursor repeated.');
      }
      seenCursors.add(cursor);
    }
    items.push(...page.items);
    onProgress?.(items.length);
  } while (cursor !== null);

  return items;
};

export const revalidateSlideCaseAssignmentOptions = async (
  request: Omit<SlideCaseAssignmentOptionsRequest, 'cursor' | 'slideIdsToInclude' | 'includeAssigned'>,
  slideIds: string[],
  signal?: AbortSignal
): Promise<SlideCaseAssignmentItem[]> => {
  const items: SlideCaseAssignmentItem[] = [];
  for (const slideIdsToInclude of chunk(slideIds, 250)) {
    items.push(
      ...(await loadSlideCaseAssignmentOptions({ ...request, includeAssigned: true, slideIdsToInclude }, signal))
    );
  }
  return items;
};
